import React, { useEffect, useMemo, useRef, useState, useContext } from "react";

// Third-parties.

import { Column, usePagination, useSortBy, useTable } from 'react-table';

import TableCSVExport from '../../../component/TableCSVExport';

// Helper.
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize } from "../../../helpers/utils";

// Types.
import Alert from '../../Alert';
import InstanceTarget from '../../../types/instance/InstanceTarget';
import WaitsDataTable from '../../../types/instance/tables/WaitsDataTable';
import { FilterType } from '../../../typescript/Enums';

// Components.
import GenericBreakdown from '../../GenericBreakdown';
import KnowledgeBaseWaitEventButton from '../../buttons/KnowledgeBaseWaitEventButton';
import ReactTable from '../../ReactTable';

// Constants.
import { DATA_LOADING } from "../../Constants";
import ChatButton from "../../chatGPT/ChatButton";
import Prompts from "../../chatGPT/Prompts";
import ConditionalRender from "../../../helpers/ConditionalRender";
import SearchInput from '../../SearchInput';
import { useLocation, useNavigate } from "react-router-dom";

// Contexts.
import { FilterContext } from '../../../context/FilterContext';

function WaitsTable(props: {
    loading: boolean | number,
    instance: InstanceTarget,
    instanceTime: number,
    waitsDataTable: WaitsDataTable[],
    setFilterOptions: Function,
    isDefault: boolean,
    currentTab?: string,
    tabIdPrefix?: string
}) {
    const tabIdPrefix = props.tabIdPrefix || '';

    const inputRef = useRef<HTMLInputElement>(null);
    const { instance, instanceTime, setFilterOptions } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(tabIdPrefix ? "" : (query.get("filter") || ""));
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    const { currentFilterOptions } = useContext(FilterContext);

    function clearTableFilter() {

        // Clear any pre-existing filter values.
        setTableFilter('');
    }

    useEffect(() => {
        // Clear Filter if tab is changed
        if (props.currentTab) {
            setTableFilter('')
        }
    }, [props.currentTab]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    const data = useMemo(() => props.waitsDataTable.filter(row => (row.waitevent.toLowerCase().includes(tableFilter.toLowerCase()))), [props.waitsDataTable, tableFilter]);

    const columns: Column[] = useMemo(() => {
        function filterData(filter: string): void {
            setFilterOptions(Helper.updateFilterOptions
            (FilterType.Waits, filter, currentFilterOptions));
        }

        return [
            {
                accessor: 'color',
                disableSortBy: true
            },
            {
                accessor: 'id',
                disableSortBy: true
            },
            {
                Header: 'Wait',
                accessor: 'waitevent',
                className: 'fw-bold',
                defaultCanSort: true,
                sortType: 'alphanumeric',
                Cell: ({ cell }) => (
                        <React.Fragment>

                            <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                            {(cell.row.values.waitevent !== '-') ? (
                                    <React.Fragment>
                                        <button key={cell.column.id} className="btn btn-sm btn-link btn-filter"
                                                onClick={() => filterData(cell.row.values.waitevent)}>{cell.row.values.waitevent}</button>
                                    </React.Fragment>
                            ) : (
                                    <React.Fragment>-</React.Fragment>
                            )}

                            <button id="btnGroupDrop1" type="button"
                                    className="btn btn-xsm btn-outline-secondary dropdown-toggle ms-1 float-end" data-bs-toggle="dropdown"
                                    aria-expanded="false">
                                {<i className="fas fa-book-reader" aria-hidden="true"></i>}
                                Find Out More
                            </button>
                            <ul className="dropdown-menu border" aria-labelledby="btnGroupDrop1">
                                <li className='dropdown-item'>
                                    <KnowledgeBaseWaitEventButton databaseType={instance.type} waitEvent={cell.row.values.waitevent}/>
                                </li>
                                <li className='dropdown-item'>
                                    <ChatButton className="btn btn-xsm float-end mx-1 float-none" value={Prompts.getInstanceWaitPrompt(cell.row.values.waitevent, instance.type)}/>
                                </li>
                            </ul>

                        </React.Fragment>
                ),
            },
            {
                Header: 'Total Time',
                accessor: 'breakdown',
                className: 'generic-waits text-nowrap',
                disableSortBy: true,
                Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                                cell.row.values.id < 20 ? (
                                        <GenericBreakdown individualTime={cell.row.values.waittime} totalTime={instanceTime} color={cell.row.values.color} tooltip={cell.row.values.waitevent}/>
                                ) : (
                                        <React.Fragment>-</React.Fragment>
                                )
                        ) : (
                                <React.Fragment>-</React.Fragment>
                        )
                )
            },
            {
                Header: 'Wait Time',
                accessor: 'waittime',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,
                sortType: (rowA, rowB, columnId) => {
                    const aWait = Number(rowA.values[columnId]) || 0;
                    const bWait = Number(rowB.values[columnId]) || 0;

                    if (aWait !== bWait) {
                        return bWait - aWait;
                    }

                    const aItem = rowA.values['waitevent'] || '';
                    const bItem = rowB.values['waitevent'] || '';
                    return aItem.localeCompare(bItem);
                },
                Cell: ({ cell }) => (
                        cell.row.values.waittime !== null ? (
                                Helper.getTimeInEnglish(cell.row.values.waittime)
                        ) : (
                                <React.Fragment>-</React.Fragment>
                        )
                )
            },
            {
                Header: 'Weight %',
                accessor: 'weight',
                headerClassName: 'text-end',
                className: 'text-end',
                defaultCanSort: true,
                sortType: 'basic',
                Cell: ({ cell }) => {
                    const { waittime } = cell.row.values;
                    const percentage = (100 / instanceTime) * waittime;

                    return (
                            <React.Fragment>
                                {instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}
                            </React.Fragment>
                    );
                }
            }
        ]
    }, [instance.type, instanceTime, currentFilterOptions, setFilterOptions]);

    const tableInstance = useTable({
                columns,
                data,
                initialState: {
                    hiddenColumns: ['id', 'color'],
                    pageIndex: initialPageIndex,
                    pageSize: initialPageSize,
                }
            },
            useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        // Don't update URL params when in panel mode (tabIdPrefix is set)
        if (tabIdPrefix) return;
        
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize, tabIdPrefix]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    const getTopWaits = () => {
        const sortedData = [...data].sort((a, b) => b.waittime - a.waittime);
        const totalTime = sortedData.reduce((total, item) => total + item.waittime, 0);

        // Calculate 80% of the total time
        const eightyPercentTotal = 0.8 * totalTime;

        // Find the top items whose total time sums 80% of the total
        let cumulativeSum = 0;
        let topItems = [];
        for (let item of sortedData) {
            cumulativeSum += item.waittime;
            topItems.push(item);
            if (cumulativeSum >= eightyPercentTotal) {
                break;
            }
        }
        return topItems.map(item => item.waitevent).slice(0, 8);
    }

    return (
            <div id={`${tabIdPrefix}waits`} className={`${props.isDefault ? 'show active' : ''} tab-pane fade`} role="tabpanel" aria-labelledby={`${tabIdPrefix}waits-tab`}>
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-clock fa-fw" aria-hidden="true"/>
                        Waits
                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseWaits" aria-expanded="false" aria-controls="collapseWaits"/>
                        <ConditionalRender if={props.waitsDataTable.length}>
                            <ChatButton className="btn btn-xsm btn-primary float-end" value={Prompts.getInstanceWaitsPrompt(getTopWaits(), instance.type)}/>
                        </ConditionalRender>
                    </div>
                    <div id="collapseWaits" className="card-body collapse show">
                        {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                                <Alert message={`To help preserve performance, we limit the total number of wait records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                                       heading="Waits records" variant="alert-info"/>
                        )}
                        <div className="row row-cols-1 row-cols-md-2 table-search">
                            <div className="col col-md-10">
                                <TableCSVExport
                                    tableInstance={tableInstance}
                                    filename="Waits"
                                    excludeColumns={['breakdown']}
                                    instanceTime={instanceTime}
                                />
                            </div>
                            <div className="col col-md-2">
                                <SearchInput
                                    ref={inputRef}
                                    value={tableFilter}
                                    onChange={(e) => setTableFilter(e.target.value)}
                                    onClear={clearTableFilter}
                                />
                            </div>
                        </div>
                        <ReactTable
                            getTableProps={getTableProps}
                            getTableBodyProps={getTableBodyProps}
                            headerGroups={headerGroups}
                            prepareRow={prepareRow}
                            page={page}
                            canPreviousPage={canPreviousPage}
                            canNextPage={canNextPage}
                            pageOptions={pageOptions}
                            pageCount={pageCount}
                            gotoPage={gotoPage}
                            nextPage={nextPage}
                            previousPage={previousPage}
                            setPageSize={setPageSize}
                            pageIndex={pageIndex}
                            pageSize={pageSize}
                            dataLength={data.length}
                            error={null}
                            loading={props.loading === DATA_LOADING || props.loading === true}
                        />
                    </div>
                </div>
            </div>
    );
}

export default WaitsTable;
